import { forwardRef, useEffect, useState } from 'react';
import {
  Button, ButtonBase,
  ButtonProps,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem
} from '@mui/material';
import { useSaveClientTagMutation } from '../shared';
import { TagSelector } from './index';
import { LoadingButton } from '@mui/lab';
import { SelectInput, TextInput } from '@coverright/ui/inputs';
import moment from 'moment';
import { ModalCloseButton } from '@coverright/ui/shared';
import * as React from 'react';
import { AssignedTagViewOutput, TagOutput } from '@coverright/data-access/types/enrollment';

const days = [0, 1, 2, 3, 4, 5, 7];

type Props = ButtonProps & {
  clientId: string;
  tags?: AssignedTagViewOutput[];
  onSubmit?: () => void
}

export const TagsFormButton = forwardRef<HTMLButtonElement, Props>(({ clientId, tags, ...props }, ref) => {
  const [open, setOpen] = useState(false);
  const [save, {loading}] = useSaveClientTagMutation({
    onCompleted: () => handleModalClose(),
    refetchQueries: ['agentDashboardCounters', 'salesQualifiedFuture', 'salesQualified', 'followUps', 'followUpsFuture', 'contacted']
  });
  const [selectedTags, setSelectedTags] = useState<Omit<TagOutput, '__typename'>[]>();
  const [expiresAt, setExpiresAt] = useState<number>(0);
  const [expiresAtCustom, setExpiresAtCustom] = useState<string>('');

  useEffect(() => {
    setSelectedTags(prev => !prev ? tags?.map(t => t.tag) : prev)
    setExpiresAt(prev => {

      if (!prev) {
        const doNotCallTag = tags?.find(t => t.expiresAt);
        if (doNotCallTag) {
          const value = Math.ceil(moment.duration(moment(doNotCallTag.expiresAt).diff(moment())).asDays());
          if (days.includes(value)) {
            return value;
          } else {
            setExpiresAtCustom(String(value))
            return -1;
          }
        }
      }
      return prev;
    })
  }, [tags]);

  const hasDoNotCallTag = selectedTags?.some(t => t.name === 'Temporary do not call');

  const handleSaveClick = () => {
    save({
      variables: {
        input: {
          tags: selectedTags?.map(t => {
            let expires: Date | undefined  = undefined;

            if (t.name === 'Temporary do not call' && expiresAt) {
              if (expiresAt === -1) {
                if (expiresAtCustom) {
                  expires = moment().add(expiresAtCustom, 'days').toDate()
                }
              } else {
                expires = moment().add(expiresAt, 'days').toDate()
              }
            }

            const assigned = tags?.find(a => a.tag.id === t.id);
            return {
              id: t.id,
              expiresAt: expires ?? assigned?.expiresAt,
              aiGenerated: assigned?.aiGenerated ?? false,
            }
          }) || [],
          clientId
        }
      }
    })
  }

  const handleModalClose = () => {
    setExpiresAt(0);
    setExpiresAtCustom('')
    setOpen(false);
  }

  const showCustomInput = !!expiresAt && !days.includes(expiresAt);

  return <>
    <Dialog fullWidth maxWidth={'xs'} open={open} onClose={() => setOpen(false)}>
      <DialogTitle variant={'h3'}>
        Tags
        <ModalCloseButton onClick={() => setOpen(false)} sx={{position: 'absolute', top: 4, right: 8}} />
      </DialogTitle>
      <DialogContent>
        <TagSelector label={'Tags'} selectedValues={selectedTags?.map(t => t.id) || []} onChange={setSelectedTags} />

        <Collapse sx={{mt: hasDoNotCallTag ? 2 : 0}} in={hasDoNotCallTag}>
          <SelectInput label={'Expires at days'}
                       hideTick
                      value={expiresAt}
                      onChange={event => {
                        setExpiresAt(event.target.value as number);
                        setExpiresAtCustom('')
                      }}
          >
            {days.map(d => (
              <MenuItem key={d} value={d}>{d ? d : '-'}</MenuItem>
            ))}
            <MenuItem value={-1}>Custom</MenuItem>
          </SelectInput>
          <Collapse sx={{mt: showCustomInput ? 2 : 0}} in={showCustomInput}>
            <TextInput hideTick onFocus={e => e.target.select()}
                       value={expiresAtCustom}
                       type={'number'}
                       onChange={e => setExpiresAtCustom(e.target.value as string)}
                       wrapperSx={{maxWidth: 100}} />
          </Collapse>
        </Collapse>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={() => setOpen(false)}
          variant={'outlined'}
        >
          Cancel
        </Button>
        <LoadingButton
          disabled={hasDoNotCallTag && !expiresAt}
          loading={loading}
          onClick={handleSaveClick}
          variant={'contained'}
        >
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
    <ButtonBase {...props} onClick={e => {
      props.onClick && props.onClick(e)
      setOpen(true)
    }} />
  </>
})
